/**
 * Rides identity document rules — client-side mirror of the backend `IdentityRuleEngine`.
 *
 * Pure, dependency-free. Use for instant inline feedback in the driver/rider onboarding forms.
 * THE SERVER IS THE SOURCE OF TRUTH: every rule re-runs in `POST api/rides/me/verification/{typeId}/start`
 * (plus the DOB / sex cross-checks against the member profile and the document-in-use check,
 * which need server data). Never persist or log a raw document number.
 *
 * Rule codes match `RIDES_RULE_CODES` (never show them raw — map to plain language in the UI).
 *
 * Self-check (run `validateSouthAfricanId('9807055009087')`):
 *   valid = true, codes = [], dateOfBirth = '1998-07-05', sexId = 1 (male), citizenship = 'citizen'
 *   (SSSS = 5009 -> male, C = 0 -> citizen, A = 8, Luhn over 980705500908 -> 7).
 */
import { RIDES_RULE_CODES } from '../../models/rides/RidesConstants';
import { RIDES_LICENCE_CODE, RIDES_LICENCE_TYPE, RIDES_SEX } from '../../models/rides/RidesConstants';
// ---------------------------------------------------------------- date helpers
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
function isValidYmd(year, month, day) {
    return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month);
}
function pad2(n) {
    return n < 10 ? `0${n}` : String(n);
}
function toYmd(year, month, day) {
    return `${year}-${pad2(month)}-${pad2(day)}`;
}
/** Parse a `yyyy-MM-dd` string into a UTC Date (null when malformed or not a real date). */
function parseYmd(value) {
    if (!value)
        return null;
    const m = DATE_RE.exec(value.trim());
    if (!m)
        return null;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (!isValidYmd(y, mo, d))
        return null;
    return new Date(Date.UTC(y, mo - 1, d));
}
function utcDateOnly(d) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}
/** Whole years between `dob` and `onDate` (both treated as calendar dates). */
function yearsBetween(dob, onDate) {
    let age = onDate.getUTCFullYear() - dob.getUTCFullYear();
    const beforeBirthday = onDate.getUTCMonth() < dob.getUTCMonth() ||
        (onDate.getUTCMonth() === dob.getUTCMonth() && onDate.getUTCDate() < dob.getUTCDate());
    if (beforeBirthday)
        age -= 1;
    return age;
}
/**
 * True when the person born on `dob` (yyyy-MM-dd or Date) is 18 or older on `onDate` (default today).
 * Returns false for an unparseable date.
 */
export function isAdult(dob, onDate = new Date()) {
    const d = dob instanceof Date ? utcDateOnly(dob) : parseYmd(dob);
    if (!d)
        return false;
    return yearsBetween(d, utcDateOnly(onDate)) >= 18;
}
// ---------------------------------------------------------------- check digits
/**
 * Luhn check digit for the first 12 digits of a South African ID number.
 * Returns -1 when `first12` is not exactly 12 digits.
 */
export function luhnCheckDigit(first12) {
    if (!/^\d{12}$/.test(first12))
        return -1;
    let sum = 0;
    // Walk right-to-left; the digit nearest the (future) check digit is doubled.
    for (let i = first12.length - 1, dbl = true; i >= 0; i--, dbl = !dbl) {
        let n = first12.charCodeAt(i) - 48;
        if (dbl) {
            n *= 2;
            if (n > 9)
                n -= 9;
        }
        sum += n;
    }
    return (10 - (sum % 10)) % 10;
}
/**
 * ICAO 9303 MRZ check digit: weights 7,3,1 repeating, mod 10; digits = value, A=10 … Z=35, '<' = 0.
 * Returns -1 when `s` contains any other character.
 */
export function mrzCheckDigit(s) {
    const weights = [7, 3, 1];
    let sum = 0;
    for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        let v;
        if (ch >= '0' && ch <= '9')
            v = ch.charCodeAt(0) - 48;
        else if (ch >= 'A' && ch <= 'Z')
            v = ch.charCodeAt(0) - 55; // A = 10
        else if (ch === '<')
            v = 0;
        else
            return -1;
        sum += v * weights[i % 3];
    }
    return sum % 10;
}
// ---------------------------------------------------------------- SA ID
/**
 * Validate a South African ID number (13 digits, `YYMMDD SSSS C A Z`).
 *
 * Rules (all mirrored server-side):
 *  - digits only, exactly 13 → `format_invalid` (stops here)
 *  - YYMMDD is a real calendar date; century inferred: YY > current two-digit year → 1900s → `date_invalid`
 *  - age on `now` within 0–130 → `age_out_of_range`
 *  - SSSS 0000–4999 = female, 5000–9999 = male (always decoded)
 *  - C ∈ {0 citizen, 1 permanent resident, 2 refugee} → `citizenship_invalid`
 *  - A ∈ {8, 9} → `digit12_invalid`
 *  - Z = Luhn check digit over the first 12 → `luhn_failed`
 *
 * Self-check: `validateSouthAfricanId('9807055009087')` → valid, 1998-07-05, male, citizen.
 */
export function validateSouthAfricanId(id, now = new Date()) {
    const codes = [];
    const value = (id ?? '').replace(/\s+/g, '');
    if (!/^\d{13}$/.test(value)) {
        return { valid: false, codes: [RIDES_RULE_CODES.Format], dateOfBirth: null, sexId: null, age: null, citizenship: null };
    }
    const today = utcDateOnly(now);
    // Date of birth with century inference.
    const yy = Number(value.slice(0, 2));
    const mm = Number(value.slice(2, 4));
    const dd = Number(value.slice(4, 6));
    const currentYY = today.getUTCFullYear() % 100;
    const century = yy > currentYY ? 1900 : 2000;
    const year = century + yy;
    let dateOfBirth = null;
    let age = null;
    if (isValidYmd(year, mm, dd)) {
        dateOfBirth = toYmd(year, mm, dd);
        age = yearsBetween(new Date(Date.UTC(year, mm - 1, dd)), today);
        if (age < 0 || age > 130)
            codes.push(RIDES_RULE_CODES.Age);
    }
    else {
        codes.push(RIDES_RULE_CODES.Date);
    }
    // Sex from the sequence number.
    const seq = Number(value.slice(6, 10));
    const sexId = seq < 5000 ? RIDES_SEX.Female : RIDES_SEX.Male;
    // Citizenship digit.
    const c = Number(value[10]);
    const citizenship = c === 0 ? 'citizen' : c === 1 ? 'permanent_resident' : c === 2 ? 'refugee' : null;
    if (citizenship === null)
        codes.push(RIDES_RULE_CODES.Citizenship);
    // Digit 12 (historic race digit; 8 or 9 on every modern ID).
    const a = Number(value[11]);
    if (a !== 8 && a !== 9)
        codes.push(RIDES_RULE_CODES.RaceDigit);
    // Luhn check digit.
    if (luhnCheckDigit(value.slice(0, 12)) !== Number(value[12]))
        codes.push(RIDES_RULE_CODES.Luhn);
    return { valid: codes.length === 0, codes, dateOfBirth, sexId, age, citizenship };
}
// ---------------------------------------------------------------- Passport
const PASSPORT_NUMBER_RE = /^[A-Z0-9]{6,9}$/;
const SA_PASSPORT_RE = /^[AMDT]\d{8}$/;
/**
 * Validate a passport for Rides verification.
 *
 *  - number: trimmed + upper-cased, 6–9 chars `A–Z0–9` (ICAO 9303 document-number slot) → `format_invalid`
 *  - issuingCountry (alpha-3) required → `country_required`
 *  - expiresAt (yyyy-MM-dd) required → `expiry_required`; must be after today → `document_expired`
 *  - optional MRZ line 2: positions 0–8 = number padded with '<', position 9 = check digit → `mrz_check_digit_failed`
 *  - ZAF passports additionally match `^[AMDT]\d{8}$` — advisory only → warning `sa_passport_pattern_warning`
 */
export function validatePassportNumber(number, issuingCountry, expiresAt, mrzLine2, now = new Date()) {
    const codes = [];
    const warnings = [];
    const normalizedNumber = (number ?? '').trim().toUpperCase();
    const country = (issuingCountry ?? '').trim().toUpperCase();
    const formatOk = PASSPORT_NUMBER_RE.test(normalizedNumber);
    if (!formatOk)
        codes.push(RIDES_RULE_CODES.Format);
    if (!/^[A-Z]{3}$/.test(country))
        codes.push(RIDES_RULE_CODES.CountryRequired);
    if (!expiresAt || !expiresAt.trim()) {
        codes.push(RIDES_RULE_CODES.ExpiryRequired);
    }
    else {
        const exp = parseYmd(expiresAt);
        if (!exp)
            codes.push(RIDES_RULE_CODES.Date);
        else if (exp.getTime() <= utcDateOnly(now).getTime())
            codes.push(RIDES_RULE_CODES.Expired);
    }
    if (formatOk && mrzLine2 && mrzLine2.trim()) {
        const line = mrzLine2.replace(/\s+/g, '').toUpperCase();
        const slot = normalizedNumber.padEnd(9, '<');
        const lineSlot = line.slice(0, 9);
        const lineCheck = line.length >= 10 ? Number(line[9]) : NaN;
        if (lineSlot !== slot || Number.isNaN(lineCheck) || mrzCheckDigit(slot) !== lineCheck) {
            codes.push(RIDES_RULE_CODES.MrzCheckDigit);
        }
    }
    if (formatOk && country === 'ZAF' && !SA_PASSPORT_RE.test(normalizedNumber)) {
        warnings.push(RIDES_RULE_CODES.PassportPatternWarning);
    }
    return { valid: codes.length === 0, codes, warnings, normalizedNumber };
}
// ---------------------------------------------------------------- Driver's licence
const ALLOWED_LICENCE_CODE_IDS = Object.values(RIDES_LICENCE_CODE);
/**
 * Validate a driver's licence for Rides verification.
 *
 *  - SA card (`licenceTypeId` = SouthAfrican): `licenceCodeId` ∈ {B, EB, C1, EC1, C, EC} → `licence_code_not_allowed`
 *  - Foreign: `country` (alpha-3) required → `country_required`; `inEnglish` or `hasIdp` → `foreign_licence_needs_english_or_idp`
 *  - both: `expiresAt` required → `expiry_required`; after today → `document_expired`
 *  - unknown `licenceTypeId` → `format_invalid`
 *
 * The holder-≥-18 rule (`driver_under_18`) needs the verified DOB and is server-side only.
 */
export function validateLicence(input, now = new Date()) {
    const codes = [];
    const warnings = [];
    if (input.licenceTypeId === RIDES_LICENCE_TYPE.SouthAfrican) {
        if (input.licenceCodeId == null || !ALLOWED_LICENCE_CODE_IDS.includes(input.licenceCodeId)) {
            codes.push(RIDES_RULE_CODES.LicenceCode);
        }
    }
    else if (input.licenceTypeId === RIDES_LICENCE_TYPE.Foreign) {
        const country = (input.country ?? '').trim().toUpperCase();
        if (!/^[A-Z]{3}$/.test(country))
            codes.push(RIDES_RULE_CODES.CountryRequired);
        if (!input.inEnglish && !input.hasIdp)
            codes.push(RIDES_RULE_CODES.ForeignLicenceNotUsable);
    }
    else {
        codes.push(RIDES_RULE_CODES.Format);
    }
    if (!input.expiresAt || !input.expiresAt.trim()) {
        codes.push(RIDES_RULE_CODES.ExpiryRequired);
    }
    else {
        const exp = parseYmd(input.expiresAt);
        if (!exp)
            codes.push(RIDES_RULE_CODES.Date);
        else if (exp.getTime() <= utcDateOnly(now).getTime())
            codes.push(RIDES_RULE_CODES.Expired);
    }
    return { valid: codes.length === 0, codes, warnings };
}
/** Namespace-style bundle of the Rides identity rules (handy for a single import). */
export const RidesIdentityRules = {
    validateSouthAfricanId,
    validatePassportNumber,
    validateLicence,
    mrzCheckDigit,
    luhnCheckDigit,
    isAdult,
};
//# sourceMappingURL=identityRules.js.map